/* @flow */
import { Link } from 'react-router-dom';
import * as React from 'react';
import styled from 'styled-components';

import { Box, Flex, Text } from '@sonyged/proc-ui';
import { Button, ButtonIcon, Footer } from '@sonyged/ext-proc';
import css from '@styled-system/css';

import { ArrowIcon, LossColor } from '../../../../common/components/ArrowIcon';
import AppContext from '../../../AppContext';
import Constants from '../../ice/fields/Constants';

type ProfitPanelProps = {
  sale: number,
  expense: number,
  profit: number,
};
const ProfitPanel = (props: ProfitPanelProps) => {
  let { sale, expense, profit } = props;
  return (
    <IcePanel>
      <IcePanelHeader>お金</IcePanelHeader>
      <Box px={[12, 12, 48]}>
        <Row mb={[12, 12, 18]} mt={[16, 16, 28]}>
          <Box bg="gray10" pl={[12, 12, 18]}>
            <Text lineHeight={['32px', '32px', '48px']}>売り上げ</Text>
          </Box>
          <Box textAlign="right">
            <Text fontSize={[20, 20, 32]}>{sale}</Text> 円
          </Box>
        </Row>

        <Row mb={[12, 12, 18]} mt={[16, 16, 28]}>
          <Box bg="gray10" pl={[12, 12, 18]}>
            <Text lineHeight={['32px', '32px', '48px']}>仕入れのねだん</Text>
          </Box>
          <Box textAlign="right">
            <Text fontSize={[20, 20, 32]}>{expense}</Text> 円
          </Box>
        </Row>

        <Line />
        <Row>
          <Box py={[16, 16, 28]}>
            <Text fontSize={[16, 16, 22]} color="secondary100">
              {profit > 0 ? 'もうけたお金' : 'そんしたお金'}
            </Text>
          </Box>
          <Box textAlign="right">
            <Text color={profit < 0 ? LossColor : null} fontSize={[30, 30, 40]}>
              {Math.abs(profit)}
            </Text>
            <Text color={profit < 0 ? LossColor : null} fontSize={[16, 16, 22]}>
              {' 円 '}
            </Text>
            <ArrowIcon fontSize={[16, 16, 22].map(x => x * 1.4)} forNum={profit} />
          </Box>
        </Row>
      </Box>
    </IcePanel>
  );
};

type SoldIcePanelProps = {
  stock: number,
  sold: number,
  remain: number,
};
const SoldIcePanel = (props: SoldIcePanelProps) => {
  let { stock, sold, remain } = props;
  return (
    <IcePanel>
      <IcePanelHeader>アイスの数</IcePanelHeader>
      <Box px={[12, 12, 48]}>
        <Row mb={[12, 12, 18]} mt={[16, 16, 28]}>
          <Box bg="gray10" pl={[12, 12, 18]}>
            <Text lineHeight={['32px', '32px', '48px']}>仕入れた数</Text>
          </Box>
          <Box textAlign="right">
            <Text fontSize={[20, 20, 32]}>{stock}</Text> こ
          </Box>
        </Row>

        <Row mb={[12, 12, 18]} mt={[16, 16, 28]}>
          <Box bg="gray10" pl={[12, 12, 18]}>
            <Text lineHeight={['32px', '32px', '48px']}>売れた数</Text>
          </Box>
          <Box textAlign="right">
            <Text fontSize={[20, 20, 32]}>{sold}</Text> こ
          </Box>
        </Row>

        <Line />
        <Row>
          <Box py={[16, 16, 28]}>
            <Text fontSize={[16, 16, 22]} color="secondary100">
              のこった数
            </Text>
          </Box>
          <Box textAlign="right">
            <Text fontSize={[30, 30, 40]}>{remain}</Text>
            <Text fontSize={[16, 16, 22]}> こ</Text>
          </Box>
        </Row>
      </Box>
    </IcePanel>
  );
};

type Props = {
  wagon: number,
  iceCtxs: any[],
  iceCtxIndex: number,
  onNextClick: Function,
  onPreviousClick: Function,
  lastPage: boolean,
  research: boolean,
};

type State = {
  iceCtxIndex: number,
};

export default class AnalysisContent extends React.Component<Props, State> {
  static contextType = AppContext;
  static defaultProps = {
    lastPage: false,
    research: false,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      iceCtxIndex: props.iceCtxIndex,
    };
  }

  render() {
    return (
      <div className={'proc-ice-field-content analysis'}>
        <div className={'proc-contents padding'}>
          <div className={'proc-outer'}>
            <div className={'proc-inner'}>
              {this._renderHeader()}
              {this._renderContent()}
            </div>
          </div>
        </div>
        {this._renderFooter()}
      </div>
    );
  }

  componentDidMount() {
    this.context.soundController.sePlay('kekkagamen');
  }

  _renderHeader() {
    let iceCtx = this._getIceCtx();
    return (
      <div className={'header'}>
        <div className={'left'}>
          <div className={'weather secondary100-fill-color'}>{Constants.getWetherSvg(iceCtx.weather)}</div>
          <div className={'temperature secondary100-color'}>
            <span className={'proc-huge-font'}>{iceCtx.temperature}</span>
            <span className={'proc-medium-font'}>度</span>
          </div>
        </div>
        <div className={'center'}>
          <img className={'image'} src={Constants.getStatusWagonIcon(this.props.wagon)} />
        </div>
        <div className={'right'}>
          <img className={'image'} src={Constants.getPlaceIcon(iceCtx.place)} />
          <img className={'image'} src={Constants.getIceImage(iceCtx.ice)} />
        </div>
      </div>
    );
  }

  _renderPreviousButton() {
    if (this.props.onPreviousClick) {
      return (
        <Button.normal onClick={this.props.onPreviousClick}>
          <ButtonIcon.back />
        </Button.normal>
      );
    }
    return <span />;
  }

  _renderNextButton() {
    if (this.props.lastPage) {
      return (
        <Link
          to={{ pathname: '/ice/fields', search: '?skip_intro=true' }}
          className={'proc-button proc-button--attention'}
        >
          しゅうりょう
        </Link>
      );
    }

    if (!this.props.onNextClick) {
      return <span />;
    }

    return <Button.attention onClick={this.props.onNextClick}>つぎへ</Button.attention>;
  }

  _renderFooter() {
    return (
      <Footer.nav className="white-bg-color" navItem={this._renderPreviousButton()}>
        {this._renderNextButton()}
      </Footer.nav>
    );
  }

  _renderContent() {
    const interest = this._getIceCtx().totalSales - this._getIceCtx().stockNum * Constants.DEFAULT_PRICE;
    const totalIceSaled = this._getIceCtx().priceSalesData.reduce((total, data) => total + data.num, 0);

    return (
      <Box className="analysis-container flex-1 hauto" px="0 !important">
        <ProfitPanel
          expense={this._getIceCtx().stockNum * Constants.DEFAULT_PRICE}
          profit={interest}
          sale={this._getIceCtx().totalSales}
        />

        <SoldIcePanel
          stock={this._getIceCtx().stockNum}
          sold={totalIceSaled}
          remain={this._getIceCtx().stockNum - totalIceSaled}
        />

        <table className={'proc-table'}>
          <thead>
            <tr>
              <th width={'20%'}>ねだん</th>
              <th width={'30%'}>売れた数</th>
              <th width={'50%'}>売り上げ</th>
            </tr>
          </thead>
          <tbody>{this._renderSales()}</tbody>
        </table>
      </Box>
    );
  }

  _renderSales() {
    return this._getIceCtx().priceSalesData.map(data => {
      return (
        <tr key={data.price}>
          <td className={`white-bg-color`}>{data.price}</td>
          <td className={`white-bg-color`}>{data.num}</td>
          <td className={'coin-container white-bg-color'}>{this._renderCoin(data)}</td>
        </tr>
      );
    });
  }

  _renderCoin(data: any) {
    let coin5Num = Math.floor(data.coin / 5);
    let coin1Num = data.coin % 5;
    const PADDING = 10;
    let images = [];
    for (let i = 0; i < coin5Num; i++) {
      let style = {
        left: PADDING + i * 13,
      };
      images.push(
        <img
          key={`5-${i}-mobile`}
          style={style}
          src="../../images/pages/ice/fields/img-coin5.png"
          className={`coin-5 proc-mobile-and-tablet-only coin-order${i}`}
        />
      );
      style = {
        left: PADDING + i * 20,
      };
      images.push(
        <img
          key={`5-${i}-large`}
          style={style}
          src="../../images/pages/ice/fields/img-coin5.png"
          className={'coin-5 proc-large-pc-only'}
        />
      );
    }
    for (let i = 0; i < coin1Num; i++) {
      let style = {
        left: PADDING + coin5Num * 13 + i * 3,
      };
      images.push(
        <img
          key={`1-${i}-mobile`}
          style={style}
          src="../../images/pages/ice/fields/img-coin1.png"
          className={'coin-1 proc-mobile-and-tablet-only'}
        />
      );
      style = {
        left: PADDING + coin5Num * 20 + i * 3,
      };
      images.push(
        <img
          key={`1-${i}-large`}
          style={style}
          src="../../images/pages/ice/fields/img-coin1.png"
          className={'coin-1 proc-large-pc-only'}
        />
      );
    }
    return <React.Fragment>{images}</React.Fragment>;
  }

  _getIceCtx() {
    return this.props.iceCtxs[this.state.iceCtxIndex];
  }

  _onChangeIndex() {
    this.setState({ iceCtxIndex: this.state.iceCtxIndex === 0 ? 1 : 0 });
  }
}

const IcePanel = styled('div')(
  css({
    borderRadius: ['8px', '8x', '15px'],
    borderStyle: 'solid',
    borderWidth: ['1px', '1px', '2px'],
    borderColor: 'secondary30',
    bg: 'white',
    overflow: 'hidden',
    mb: '10px',
  })
);

const IcePanelHeader = styled('div')(
  css({
    color: 'secondary100',
    bg: 'secondary20',
    fontSize: ['14px', '14px', '20px'],
    padding: '4px 16px',
  })
);

const Line = styled(Box)(
  css({
    height: '2px',
    bg: 'secondary20',
  })
);

const Row = styled(Flex)(
  css({
    fontSize: [14, 14, 20],
    color: 'gray100',
    '>*': { flex: 1 },
  })
);
